import { useCallback, useMemo } from 'react';
import type { BasicRecord, SCAdvanceTableProps } from './types';
import { EnumTableFieldType } from './types';

const selectFieldName = '__isSelected';

function useSelect<D extends BasicRecord>({
  Selectable,
  Selected,
  OnSelect,
  Rows: _Rows,
  Columns,
  ChangeValue,
  ...rest
}: SCAdvanceTableProps<D>) {
  const newRows = useMemo(() => {
    const Rows = _Rows || [];
    if (!Selectable) {
      return Rows;
    }
    return Rows.map((row) => {
      return {
        ...row,
        [selectFieldName]: Selected?.includes(row[rest.IdField]),
      };
    });
  }, [Selectable, _Rows, Selected, rest.IdField]);

  const isNotAllSelected = useMemo(() => {
    return newRows.find((row) => row[selectFieldName] !== true);
  }, [newRows]);

  const toggleCheckboxValue = useCallback(() => {
    if (isNotAllSelected) {
      OnSelect?.(newRows.map((row) => row[rest.IdField]));
    } else {
      OnSelect?.([]);
    }
  }, [OnSelect, isNotAllSelected, newRows, rest.IdField]);

  const newColumns = useMemo(() => {
    if (!Selectable) {
      return Columns;
    }

    return [
      {
        Label: 'Select',
        FieldName: selectFieldName,
        FieldType: EnumTableFieldType.Checkbox,
        Editable: true,
        Alignment: 1,
        Width: 26,
        RenderLabel: () => {
          return (
            <div>
              <input type="checkbox" onChange={toggleCheckboxValue} checked={!isNotAllSelected} />
            </div>
          );
        },
      },
      ...Columns,
    ];
  }, [Columns, Selectable, isNotAllSelected, toggleCheckboxValue]);

  const newChangeValue = useCallback(
    (idField: string, field: string, value: any) => {
      if (field === selectFieldName) {
        const newSelected =
          value === true
            ? [...(Selected || []), idField]
            : (Selected || []).filter((id) => id !== idField);
        OnSelect?.(newSelected);
        return;
      }

      if (ChangeValue) {
        ChangeValue(idField, field, value);
      }
    },
    [ChangeValue, OnSelect, Selected],
  );

  return {
    ...rest,
    Rows: newRows,
    Columns: newColumns,
    ChangeValue: newChangeValue,
  };
}

export { useSelect };
